import { EditorState } from "draft-js";
import { NextPageWithLayout } from "next";
import { useRouter } from "next/router";
import Script from "next/script";
import {
  CSSProperties,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ServiceViewType } from "@/apis/clients";
import { ApiResponseError } from "@/apis/errors";
import EditorIframeDisplay, {
  EditorDisplayOptions,
} from "@/components/editors/EditorIframeDisplay";
import useApiResponseErrorHandler from "@/hooks/useApiResponseErrorHandler";
import { Device } from "@/models/editors/editor";
import { useLoading } from "@/states/loading/operations";
import { TemplatePart } from "@/states/page-editor/template-parts/types";
import { useFetchServiceFormByService } from "@/states/service-form/operations";
import { useServiceInfo } from "@/states/service-form/selectors";
import { getDefaultIframeBlock } from "@/utils/common";
import { convertFromRawRecursively } from "@/utils/convert-page-json";
import { makeLinkDecorator } from "@/utils/draft";

// 通常テキストにリンクが貼られた時用のDecorator
const linkDecorator = makeLinkDecorator();
export interface State {
  isReady: boolean;
  device: Device;
  pageStyleSettings: CSSProperties;
  editorState: EditorState;
  mainBlock?: TemplatePart;
}

const PageIndex: NextPageWithLayout = () => {
  const [state, setState] = useState<State>({
    isReady: false,
    device: Device.DESKTOP,
    pageStyleSettings: {},
    editorState: EditorState.createEmpty(),
  });

  const router = useRouter();
  const withLoading = useLoading();
  const { handlePageError } = useApiResponseErrorHandler();
  const fetchServiceFormByService = useFetchServiceFormByService();
  const service = useServiceInfo();

  const serviceId = useMemo(() => {
    const { uuid } = router.query;
    if (uuid === undefined || typeof uuid !== "string") {
      return undefined;
    }
    return Number(uuid);
  }, [router.query]);

  const editorDisplayOptions: EditorDisplayOptions = useMemo(() => {
    const formPath =
      serviceId && service?.viewType === ServiceViewType.BUTTON
        ? `/form/${serviceId}?iframeId=${serviceId}&from=${ServiceViewType.BUTTON}`
        : "";
    const confirmPath = serviceId
      ? `/form/${serviceId}/confirm?iframeId=${serviceId}&from=${ServiceViewType.FORM}`
      : "";

    return {
      partsProps: {
        ctaButtonService: {
          formPath,
          confirmPath,
        },
        serviceViewType: service?.viewType,
      },
    };
  }, [serviceId, service]);

  useEffect(() => {
    if (service && service.type) {
      const defaultContentState = getDefaultIframeBlock(service.type);
      // JSONからContentStateを復元してEditorStateを初期化する
      const initialEditorState = EditorState.createWithContent(
        convertFromRawRecursively(defaultContentState),
        linkDecorator,
      );

      setState((prev) => ({
        ...prev,
        editorState: initialEditorState,
        isReady: true,
      }));
    }
  }, [service]);

  useEffect(() => {
    if (serviceId === undefined) {
      return;
    }

    void (async () => {
      await withLoading(async () => {
        try {
          await fetchServiceFormByService(serviceId);
        } catch (e) {
          if (e instanceof ApiResponseError) {
            await handlePageError(e);
          }

          throw e;
        }
      });
    })();
  }, [serviceId, fetchServiceFormByService, handlePageError, withLoading]);

  if (!state.isReady) {
    return <></>;
  }

  return (
    <>
      <Script src="/customer/assets/yoyacoo.js"></Script>
      <div
        id="yoyacoo"
        className="flex flex-col items-center min-w-full"
        style={state.pageStyleSettings}
      >
        <div className="px-4 space-y-10 w-full max-w-[920px] md:px-0">
          {state.editorState && (
            <EditorIframeDisplay
              state={state.editorState}
              options={editorDisplayOptions}
              data-testid="editor-display"
            />
          )}
        </div>
      </div>
    </>
  );
};

PageIndex.getLayout = function getLayout(children: ReactElement) {
  return (
    <div className="overflow-hidden">
      <div className="min-w-full min-h-screen">{children}</div>
    </div>
  );
};

export default PageIndex;
